/**
 * React hook for nonce management
 * Provides nonce query, reservation, and lifecycle management
 * Uses NonceManager for Ethereum-aligned nonce lifecycle
 */

import { useState, useCallback, useRef } from 'react';
import { InfoClient } from '../sdk';
import { NonceManager, NonceQueryResult, type NonceStorageAdapter } from '../sdk/utils/nonceManager';
import { useNonceStore } from '../store';

export interface UseNonceResult {
  nextNonce: number | null;
  loading: boolean;
  error: string | null;
  queryNonce: (reserve?: boolean) => Promise<NonceQueryResult | null>;
  reserveNonce: (nonce: number, isOffChain?: boolean) => Promise<void>;
  commitNonce: (nonce: number, isOffChain?: boolean) => Promise<void>;
  rejectNonce: (nonce: number, isOffChain?: boolean) => Promise<void>;
  reset: () => void;
}

/**
 * Hook for nonce management
 * @param baseURL - Base URL for API requests
 * @param walletAddress - Optional wallet address (if not provided, must be set before operations)
 * @returns UseNonceResult with nonce state and methods
 */
export function useNonce(baseURL: string, walletAddress?: string): UseNonceResult {
  const [nextNonce, setNextNonce] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use ref to store NonceManager instance (persists across renders)
  const nonceManagerRef = useRef<NonceManager | null>(null);
  const infoClientRef = useRef<InfoClient | null>(null);

  // Initialize NonceManager if not already created (with nonceStore for browser persistence)
  const getNonceManager = useCallback((): NonceManager => {
    if (!nonceManagerRef.current) {
      if (!infoClientRef.current) {
        infoClientRef.current = new InfoClient({ baseURL });
      }
      const store = useNonceStore.getState();
      const adapter: NonceStorageAdapter = {
        getSnapshot: (owner) => store.getNonceState(owner),
        setSnapshot: (owner, state) => store.setNonceState(owner, state),
        clearOwner: (owner) => store.clearOwner(owner),
      };
      nonceManagerRef.current = new NonceManager(infoClientRef.current, adapter);
    }
    return nonceManagerRef.current;
  }, [baseURL]);

  /**
   * Query nonce status from server
   * @param reserve - Whether to reserve the nonce atomically
   * @returns Nonce query result or null on error
   */
  const queryNonce = useCallback(async (reserve: boolean = false): Promise<NonceQueryResult | null> => {
    if (!walletAddress) {
      setError('Wallet address not provided');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const nonceManager = getNonceManager();
      const result = await nonceManager.getExpectedNextNonce(walletAddress, reserve);
      
      setNextNonce(result.next_nonce);
      return result;
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to query nonce';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [walletAddress, getNonceManager]);

  /**
   * Reserve a nonce (validate and add to pending)
   * @param nonce - Nonce value to reserve
   * @param isOffChain - Whether this is an off-chain operation
   */
  const reserveNonce = useCallback(async (nonce: number, isOffChain: boolean = false): Promise<void> => {
    if (!walletAddress) {
      throw new Error('Wallet address not provided');
    }

    setLoading(true);
    setError(null);

    try {
      const nonceManager = getNonceManager();
      await nonceManager.validateNonce(walletAddress, nonce, isOffChain);
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to reserve nonce';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [walletAddress, getNonceManager]);

  /**
   * Commit a nonce after successful execution
   * @param nonce - Nonce value to commit
   * @param isOffChain - Whether this is an off-chain operation
   */
  const commitNonce = useCallback(async (nonce: number, isOffChain: boolean = false): Promise<void> => {
    if (!walletAddress) {
      throw new Error('Wallet address not provided');
    }

    setLoading(true);
    setError(null);

    try {
      const nonceManager = getNonceManager();
      await nonceManager.commitNonce(walletAddress, nonce, isOffChain);
      
      // Update next nonce after commit
      const result = await nonceManager.getExpectedNextNonce(walletAddress, false);
      setNextNonce(result.next_nonce);
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to commit nonce';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [walletAddress, getNonceManager]);

  /**
   * Reject a nonce on failure (allows retry)
   * @param nonce - Nonce value to reject
   * @param isOffChain - Whether this is an off-chain operation
   */
  const rejectNonce = useCallback(async (nonce: number, isOffChain: boolean = false): Promise<void> => {
    if (!walletAddress) {
      throw new Error('Wallet address not provided');
    }

    setLoading(true);
    setError(null);

    try {
      const nonceManager = getNonceManager();
      await nonceManager.rejectNonce(walletAddress, nonce, isOffChain);
      
      // Update next nonce after reject (may allow retry with same nonce)
      const result = await nonceManager.getExpectedNextNonce(walletAddress, false);
      setNextNonce(result.next_nonce);
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to reject nonce';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [walletAddress, getNonceManager]);

  /**
   * Reset nonce state
   */
  const reset = useCallback(() => {
    if (walletAddress && nonceManagerRef.current) {
      nonceManagerRef.current.resetNonce(walletAddress);
    }
    setNextNonce(null);
    setError(null);
    setLoading(false);
  }, [walletAddress]);

  return {
    nextNonce,
    loading,
    error,
    queryNonce,
    reserveNonce,
    commitNonce,
    rejectNonce,
    reset,
  };
}
